/**
 * DiagnosisPreview - Premium Interactive Preview Component
 * 
 * Features annotated image with pulsing hotspots on problem areas,
 * hover tooltips, and large confidence percentages.
 */

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, Activity, ArrowRight, FileText } from 'lucide-react';
import { DiagnosisResult, DiagnosisIssue } from './CitrusDoctor';
import { UploadedImage } from './ImageUpload';

interface DiagnosisPreviewProps {
  diagnosis: DiagnosisResult;
  onContinue: () => void;
  onBack: () => void;
  images?: UploadedImage[];
}

// Predefined hotspot positions based on issue keywords
const getHotspotPosition = (issue: DiagnosisIssue, index: number): { top: string; left: string } => {
  const name = issue.name.toLowerCase();
  
  // Position based on plant part mentioned
  if (name.includes('leaf') || name.includes('chlorosis') || name.includes('foliage')) {
    return { top: '25%', left: `${30 + index * 20}%` };
  }
  if (name.includes('root') || name.includes('soil') || name.includes('potting')) {
    return { top: '75%', left: `${35 + index * 15}%` };
  }
  if (name.includes('trunk') || name.includes('stem') || name.includes('bark')) {
    return { top: '50%', left: '50%' };
  }
  if (name.includes('fruit') || name.includes('flower') || name.includes('blossom')) {
    return { top: '30%', left: '60%' };
  }
  if (name.includes('water') || name.includes('moisture') || name.includes('drought')) {
    return { top: '60%', left: `${25 + index * 25}%` };
  }
  if (name.includes('pest') || name.includes('insect') || name.includes('mite')) {
    return { top: '35%', left: '70%' };
  }
  if (name.includes('disease') || name.includes('fungus') || name.includes('infection')) {
    return { top: '40%', left: '40%' };
  }
  
  // Default positions spread around the image
  const positions = [
    { top: '30%', left: '35%' },
    { top: '55%', left: '60%' },
    { top: '70%', left: '40%' },
    { top: '25%', left: '65%' },
  ];
  return positions[index % positions.length];
};

const DiagnosisPreview: React.FC<DiagnosisPreviewProps> = ({
  diagnosis,
  onContinue,
  onBack,
  images = []
}) => {
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);

  // Toggle issue selection - click same to close, click different to switch
  const handleIssueClick = (issueId: string) => {
    setSelectedIssue(prev => prev === issueId ? null : issueId);
  };

  const getHealthStatus = () => {
    switch (diagnosis.overallHealth) {
      case 'healthy':
        return {
          label: 'Healthy',
          color: 'status-healthy',
          icon: CheckCircle,
          message: 'Your tree is in excellent condition!'
        };
      case 'minor_issues':
        return {
          label: 'Minor Issues',
          color: 'status-minor',
          icon: AlertCircle,
          message: 'A few things need your attention.'
        };
      case 'moderate_issues':
        return {
          label: 'Needs Attention',
          color: 'status-moderate',
          icon: AlertTriangle,
          message: 'Several issues detected.'
        };
      case 'severe_issues':
        return {
          label: 'Urgent Care Needed',
          color: 'status-severe',
          icon: AlertTriangle,
          message: 'Immediate action needed!'
        };
      default:
        return {
          label: 'Analysis Complete',
          color: 'status-moderate',
          icon: Activity,
          message: 'Your results are ready.'
        };
    }
  };

  const healthStatus = getHealthStatus();
  const StatusIcon = healthStatus.icon;
  const previewIssues = diagnosis.issues.slice(0, 4);
  const productRecommendations = diagnosis.recommendations.filter(r => r.productRecommendation);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f97316';
      case 'low': return '#eab308';
      default: return '#9ca3af';
    }
  };

  // Get related recommendation for an issue
  const getRecommendationForIssue = (issue: DiagnosisIssue) => {
    return diagnosis.recommendations.find(r => 
      r.action.toLowerCase().includes(issue.name.toLowerCase().split(' ')[0]) ||
      r.reasoning.toLowerCase().includes(issue.name.toLowerCase().split(' ')[0])
    ) || diagnosis.recommendations[0];
  };

  return (
    <div className="doctor-step doctor-step-preview">
      {/* Two Column Layout */}
      <div className="preview-layout">
        {/* Left: Image with Hotspots */}
        <div className="preview-image-section">
          <div className="preview-image-container-compact">
            {images.length > 0 ? (
              <img 
                src={images[0].previewUrl} 
                alt="Your citrus tree" 
                className="preview-main-image-compact"
              />
            ) : (
              <div className="preview-image-placeholder">
                <span>🌳</span>
              </div>
            )}
            
            {/* Pulsing Hotspots - Click or keyboard to select */}
            {previewIssues.map((issue, index) => {
              const position = getHotspotPosition(issue, index);
              const isSelected = selectedIssue === issue.id;
              
              return (
                <div
                  key={issue.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Issue ${index + 1}: ${issue.name}. ${issue.severity} severity, ${issue.confidence}% confidence. Press Enter to view details.`}
                  aria-pressed={isSelected}
                  className={`hotspot-marker ${isSelected ? 'active' : ''} severity-${issue.severity}`}
                  style={{ 
                    top: position.top, 
                    left: position.left,
                    zIndex: isSelected ? 100 : 10 - index 
                  }}
                  onClick={() => handleIssueClick(issue.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleIssueClick(issue.id);
                    }
                  }}
                >
                  <div className="hotspot-pulse" />
                  <div className="hotspot-dot" />
                  <span className="hotspot-number">{index + 1}</span>
                </div>
              );
            })}
          </div>
          
          {/* Health Score Badge */}
          <div className={`preview-health-badge ${healthStatus.color}`}>
            <span className="health-score-number">{diagnosis.healthScore}</span>
            <span className="health-score-label">Health Score</span>
            <div className={`health-status-tag ${healthStatus.color}`}>
              <StatusIcon size={14} />
              <span>{healthStatus.label}</span>
            </div>
          </div>
        </div>

        {/* Right: Issues List or Zoom Popup */}
        <div className="preview-details-section">
          {selectedIssue ? (
            // Zoom Popup with Issue Details
            (() => {
              const issue = previewIssues.find(i => i.id === selectedIssue);
              if (!issue) return null;
              const position = getHotspotPosition(issue, previewIssues.indexOf(issue));
              const recommendation = getRecommendationForIssue(issue);
              
              return (
                <div 
                  className="zoom-popup"
                  role="dialog"
                  aria-labelledby="zoom-title"
                  aria-modal="true"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setSelectedIssue(null);
                    }
                  }}
                >
                  {/* Close Button */}
                  <button 
                    className="zoom-close-btn"
                    onClick={() => setSelectedIssue(null)}
                    aria-label="Close issue details"
                    autoFocus
                  >
                    ✕
                  </button>
                  
                  {/* Zoomed Image Area */}
                  <div className="zoom-image-container">
                    {images.length > 0 && (
                      <div 
                        className="zoom-image"
                        style={{
                          backgroundImage: `url(${images[0].previewUrl})`,
                          backgroundPosition: `${position.left} ${position.top}`,
                          backgroundSize: '250%'
                        }}
                      />
                    )}
                    <div className="zoom-focus-indicator" />
                  </div>
                  
                  {/* Issue Details */}
                  <div className="zoom-details">
                    <div className="zoom-header">
                      <span 
                        className="zoom-severity-badge"
                        style={{ background: getSeverityColor(issue.severity) }}
                      >
                        {issue.severity.toUpperCase()} PRIORITY
                      </span>
                      <span className="zoom-confidence">{issue.confidence}% confidence</span>
                    </div>
                    <h3 id="zoom-title" className="zoom-title">{issue.name}</h3>
                    <p className="zoom-description">{issue.description}</p>
                    
                    {/* Recommendation */}
                    {recommendation && (
                      <div className="zoom-recommendation">
                        <h4>💡 Recommended Action</h4>
                        <p>{recommendation.action}</p>
                        {recommendation.productRecommendation && (
                          <a 
                            href={recommendation.productRecommendation.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="zoom-product-link"
                          >
                            {recommendation.productRecommendation.name}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            // Default: Issues List
            <div className="preview-issues-list">
              <h3 className="issues-list-title">Detected Issues</h3>
              <p className="issues-list-hint">Click on numbered markers to zoom in</p>
              
              <div className="issues-cards">
                {previewIssues.map((issue, index) => (
                  <div 
                    key={issue.id}
                    className={`issue-card severity-${issue.severity}`}
                    onClick={() => handleIssueClick(issue.id)}
                  >
                    <div 
                      className="issue-number"
                      style={{ background: getSeverityColor(issue.severity) }}
                    >
                      {index + 1}
                    </div>
                    <div className="issue-info">
                      <span className="issue-name">{issue.name}</span>
                      <span className="issue-meta">
                        <span className={`issue-severity severity-${issue.severity}`}>
                          {issue.severity}
                        </span>
                        <span className="issue-confidence">{issue.confidence}%</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {diagnosis.issues.length > 4 && (
                <div className="issues-more">
                  +{diagnosis.issues.length - 4} more issues in full report
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="preview-cta-section">
        <button className="preview-cta-primary" onClick={onContinue}>
          <span>Get My Full Report</span>
          <ArrowRight size={20} />
        </button>

        {/* What's Included - Centered */}
        <div className="preview-includes-compact" style={{ textAlign: 'center', margin: '0.75rem auto' }}>
          <div className="includes-header" style={{ justifyContent: 'center' }}>
            <FileText size={20} />
            <span>Your Full Report Includes</span>
          </div>
          <div className="includes-list" style={{ justifyContent: 'center' }}>
            <span>✅ {diagnosis.recommendations.length} action steps</span>
            {productRecommendations.length > 0 && <span>🧪 Product recommendations</span>}
            <span>🌱 Expert care guide</span>
          </div>
        </div>

        <button className="preview-cta-secondary" onClick={onBack}>
          Start Over
        </button>
      </div>
    </div>
  );
};

export default DiagnosisPreview;
